import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NextUIProvider } from "@nextui-org/react";
import { createRouter } from "@/routing/Router";
import { ThemeProvider } from "./providers/ThemeProvider";

// ---------------------- Variables

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Number(import.meta.env.VITE_QUERY_STALE_TIME_MINUTES) * 60 * 1000,
      gcTime: Number(import.meta.env.VITE_QUERY_GC_TIME_MINUTES) * 60 * 1000,
    },
  },
});

const enableDevTools =
  import.meta.env.VITE_QUERY_ENABLE_DEVTOOLS === "true" &&
  import.meta.env.VITE_CURRENT_MODE !== "production";

// Router is created once at module level — creating it inside the component
// causes a new router instance (and re-fired loaders) on every App re-render
const router = createRouter(queryClient);

// ---------------------- Component

export default function App() {
  // --------- Helpers

  const getHref = (path: string) => {
    if (path.startsWith("/")) {
      return path;
    }

    const currentPath = router.state.location.pathname;

    return `${currentPath}/${path}`;
  };

  // --------- JSX

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {enableDevTools && (
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
        )}

        <NextUIProvider
          navigate={router.navigate}
          useHref={getHref}
        >
          <RouterProvider router={router} />
        </NextUIProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
